import { useProjectSettings, useThemeContext } from '@mobile-mapping/providers';
import { BtnClasses, IconButton, PanelLayout } from '@tcweb/connect-ui';
import { AlignmentSettingsPanel } from '@trimble/alignment-navigation';
import { t } from '@trimble/i18n';
import { FormattingSettings } from '@trimble/ui-components/solidjs';
import {
  SettingsAccordionSection,
  SettingsSection,
  ViewerPreferencesSettings,
} from '@trimble/viewer-settings';
import { createSignal, type JSX, Show } from 'solid-js';
import { DebugSettings } from './DebugSettings';

export interface SettingsTabProps {
  onClose(): void;
}

const TITLE_DATA_CY = 'settings-tab-title';

export const SettingsTab = (props: SettingsTabProps): JSX.Element => {
  const {
    formattingSettings,
    updateFormattingSettings,
    slopeMeasurementSettings,
    updateSlopeMeasurementSettings,
    alignmentSettings,
    updateAlignmentSettings,
    refreshAlignmentData,
  } = useProjectSettings();
  const { preferenceSettings, setPreferenceSettings } = useThemeContext();
  const [enableDebugSection, setEnableDebugSection] = createSignal(false);

  const handlePanelClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    const titleElement = target.closest(`[data-cy="${TITLE_DATA_CY}"]`);

    if (titleElement && event.shiftKey && event.altKey) {
      event.preventDefault();
      event.stopPropagation();
      window.getSelection()?.removeAllRanges();
      setEnableDebugSection(true);
    }
  };

  return (
    <div data-testid="settings-tab" class="contents" onClick={handlePanelClick}>
      <PanelLayout
        panelTitle={t('mobileMapping.settings.title')}
        titleDataCy={TITLE_DATA_CY}
        rightActions={
          <IconButton
            color={BtnClasses.TERTIARY}
            icon="tc-icon-content-close"
            size="icon-medium"
            data-cy="settings-tab-close"
            clicked={props.onClose}
          />
        }
        content={
          <>
            <Show when={enableDebugSection()}>
              <SettingsAccordionSection
                title={t('mobileMapping.settings.debugSettings')}
                dataTestId="debug-settings-section"
                defaultExpanded
              >
                <DebugSettings />
              </SettingsAccordionSection>
            </Show>
            <SettingsAccordionSection
              title={t('mobileMapping.settings.preferences')}
              dataTestId="preferences-settings-section"
            >
              <ViewerPreferencesSettings
                preferenceSettings={preferenceSettings}
                setPreferenceSettings={setPreferenceSettings}
              />
            </SettingsAccordionSection>
            <SettingsAccordionSection
              title={t('mobileMapping.settings.unitSettings')}
              dataTestId="uom-settings-section"
            >
              <SettingsSection>
                <FormattingSettings
                  settings={formattingSettings()}
                  onSettingsChanged={(nextSettings) =>
                    void updateFormattingSettings(nextSettings)
                  }
                  slopeMeasurementSettings={slopeMeasurementSettings()}
                  onSlopeMeasurementSettingsChanged={
                    updateSlopeMeasurementSettings
                  }
                />
              </SettingsSection>
            </SettingsAccordionSection>
            <SettingsAccordionSection
              title={t('mobileMapping.settings.alignmentSettings')}
              dataTestId="alignment-settings-section"
              onExpand={refreshAlignmentData}
            >
              <AlignmentSettingsPanel
                settings={alignmentSettings()}
                onSettingsChanged={updateAlignmentSettings}
                lengthUnit={formattingSettings().lengthUnit}
              />
            </SettingsAccordionSection>
          </>
        }
      />
    </div>
  );
};
